import { staffImportRowOutcomes } from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";

import { useReportDirty } from "../../catalog/unsaved";
import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { cn } from "../../ui/cn";
import { EmptyState } from "../../ui/empty-state";
import { BackLink } from "../../ui/links";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { SectionCard } from "../../ui/section-card";
import { StatusMessage } from "../../ui/status-message";
import type { SaveMessage } from "../commands";
import { JobStatusBadge } from "../components/parts";
import { rawText } from "../components/styles";
import { describeImportError } from "../errors";
import { useImportsRuntime } from "../runtime";
import { useReviewCommands } from "./commands";
import { ApprovalPanel } from "./components/ApprovalPanel";
import { BulkBar } from "./components/BulkBar";
import { FilterBar } from "./components/FilterBar";
import { ProgressView } from "./components/ProgressView";
import { RowDrawer } from "./components/RowDrawer";
import { RowTable } from "./components/RowTable";
import type { RowDrafts } from "./decisions";
import { jobFailureMessages, processingFilterOptions, type RowOutcome } from "./labels";
import { jobPhase } from "./progress";
import {
  DEFAULT_PROGRESS_POLL_MS,
  type ReviewFilter,
  reviewKeys,
  useImportProgress,
  useReviewRows,
} from "./queries";

// The review screen of a validated import: `/imports/:jobId/review`. One page for every stage after the
// mapping was submitted. What it shows follows the job: a job that is validating shows a waiting notice,
// a job in review shows the approval panel, the bulk actions, the row table and the row drawer, and a job
// that was approved shows the progress and the result of each row. The filter and the open row live in the
// address, so the browser's Back button and a shared link work.

const isOutcome = (value: string | null): value is RowOutcome =>
  value !== null && (staffImportRowOutcomes as readonly string[]).includes(value);

function filterOf(search: URLSearchParams): ReviewFilter {
  const outcome = search.get("status");
  const processing = search.get("result");
  return {
    outcome: isOutcome(outcome) ? outcome : null,
    needsDecision: search.get("needs") === "1",
    processing:
      processingFilterOptions.find((option) => option.value === processing)?.value ?? null,
  };
}

export function ImportReviewPage() {
  const { jobId } = useParams();
  if (jobId === undefined) return <Navigate to="/imports" replace />;
  return <ReviewScreen key={jobId} jobId={jobId} />;
}

function ReviewScreen({ jobId }: Readonly<{ jobId: string }>) {
  useWideLayout();
  const { progressPollMs = DEFAULT_PROGRESS_POLL_MS } = useImportsRuntime();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const progress = useImportProgress(jobId, progressPollMs);
  const commands = useReviewCommands(jobId);
  const [message, setMessage] = useState<SaveMessage | null>(null);
  const [drafts, setDrafts] = useState<Readonly<Record<string, RowDrafts>>>({});
  const opener = useRef<HTMLButtonElement | null>(null);
  const tableHeading = useRef<HTMLHeadingElement>(null);

  const data = progress.data;
  const phase = data === undefined ? null : jobPhase(data.job);
  const inReview = phase === "review";
  const filter = useMemo(() => filterOf(search), [search]);
  const rowId = search.get("row");
  const hasRows =
    phase === "review" || phase === "processing" || phase === "done" || phase === "failed";
  // Only the filter that fits the stage is applied: the address may still hold the review filters after
  // the import was approved, and the result filter has no meaning during review.
  const mode = inReview ? "review" : "processing";
  const effectiveFilter: ReviewFilter =
    mode === "review"
      ? { ...filter, processing: null }
      : { ...filter, outcome: null, needsDecision: false };
  const rows = useReviewRows(jobId, effectiveFilter, hasRows);
  const rowList = useMemo(() => rows.data?.pages.flatMap((page) => page.rows) ?? [], [rows.data]);

  // The choices that are not saved yet. Leaving the page asks first, as the mapping step does.
  const dirty = Object.values(drafts).some((entry) => Object.keys(entry).length > 0);
  useReportDirty("import-review", dirty);

  function patchSearch(change: (next: URLSearchParams) => void) {
    setSearch(
      (current) => {
        const next = new URLSearchParams(current);
        change(next);
        return next;
      },
      { replace: true },
    );
  }

  function changeFilter(next: ReviewFilter) {
    patchSearch((params) => {
      for (const key of ["status", "needs", "result"]) params.delete(key);
      if (next.outcome !== null) params.set("status", next.outcome);
      if (next.needsDecision) params.set("needs", "1");
      if (next.processing !== null) params.set("result", next.processing);
      params.delete("row");
    });
  }

  function openRow(id: string, button: HTMLButtonElement) {
    opener.current = button;
    patchSearch((params) => params.set("row", id));
  }

  function closeRow() {
    patchSearch((params) => params.delete("row"));
    // The button that opened the row gets the focus back. When the table changed, the heading does.
    requestAnimationFrame(() => {
      const button = opener.current;
      if (button?.isConnected) button.focus();
      else tableHeading.current?.focus();
    });
  }

  // After a retry the page of the new job opens.
  function openRetried(newJobId: string) {
    void queryClient.invalidateQueries({ queryKey: reviewKeys.progress(newJobId) });
    navigate(`/imports/${encodeURIComponent(newJobId)}/review`);
  }

  // The notice of an action (such as "approved, processing now") is about the stage it was made in.
  useEffect(() => {
    if (phase === "done" || phase === "failed") setMessage(null);
  }, [phase]);

  // A job that is still a draft has no review: the wizard shows it.
  if (phase === "draft") return <Navigate to={`/imports/${encodeURIComponent(jobId)}`} replace />;

  if (progress.isPending) {
    return (
      <AsyncState
        kind="loading"
        title="Loading the import"
        message="Reading the state of the import."
      />
    );
  }
  if (data === undefined) {
    return (
      <AsyncState
        kind="error"
        title="The import could not be loaded"
        message={describeImportError(progress.error).message}
        onRetry={() => void progress.refetch()}
        accessory={<BackLink to="/imports">Back to imports</BackLink>}
      />
    );
  }

  const { job } = data;
  const drawer =
    rowId === null || !hasRows ? null : (
      <RowDrawer
        key={rowId}
        jobId={jobId}
        rowId={rowId}
        editable={inReview}
        drafts={drafts[rowId] ?? {}}
        onDrafts={(next) => setDrafts((current) => ({ ...current, [rowId]: next }))}
        commands={commands}
        onMessage={setMessage}
        onClose={closeRow}
      />
    );

  return (
    <div className="grid min-w-0 gap-6">
      <PageHeader
        title="Review import"
        description={
          <>
            File: <span className={cn(rawText, "font-mono text-sm")}>{job.fileName}</span>.{" "}
            <JobStatusBadge job={job} />
          </>
        }
        breadcrumbs={<BackLink to="/imports">Back to imports</BackLink>}
      />

      <StatusMessage message={message} />

      {phase === "validating" ? (
        <SectionCard id="import-validating" title="Checking the rows">
          <p role="status" className="m-0">
            The server is checking every row and looking for items that already exist. This page
            updates by itself. Nothing has reached the catalog.
          </p>
        </SectionCard>
      ) : null}

      {phase === "closed" ? (
        <Notice tone="info" role="status">
          {jobFailureMessages.REJECTED_BY_STAFF}
        </Notice>
      ) : null}

      {phase === "review" ? (
        <>
          <ApprovalPanel progress={data} commands={commands} onMessage={setMessage} />
          <BulkBar commands={commands} onMessage={setMessage} />
        </>
      ) : null}

      {phase === "processing" || phase === "done" || phase === "failed" ? (
        <ProgressView
          progress={data}
          refreshing={progress.isFetching}
          onRefresh={() => void progress.refetch()}
          commands={commands}
          onMessage={setMessage}
          onRetried={openRetried}
        />
      ) : null}

      {hasRows ? (
        <section className="grid min-w-0 gap-4" aria-labelledby="import-rows-title">
          <h2
            id="import-rows-title"
            tabIndex={-1}
            ref={tableHeading}
            className="m-0 font-serif text-[26px] leading-8 font-medium"
          >
            Rows
          </h2>
          <FilterBar filter={effectiveFilter} mode={mode} onChange={changeFilter} />
          {/* The drawer comes first in the page, so a keyboard user meets it before the table and a narrow screen shows it above the rows.
              Only a wide screen has room to put it beside the table. */}
          <div
            className={cn(
              "grid min-w-0 grid-cols-[minmax(0,1fr)] items-start gap-6",
              drawer !== null &&
                "min-[1680px]:grid-cols-[minmax(0,1fr)_minmax(26rem,35rem)] min-[1680px]:[&>aside]:order-2",
            )}
          >
            {drawer}
            <div className="min-w-0" aria-busy={rows.isFetching}>
              {rows.isPending ? (
                <AsyncState
                  kind="loading"
                  title="Loading the rows"
                  message="Reading the first page."
                />
              ) : rows.isError ? (
                <AsyncState
                  kind="error"
                  title="The rows could not be loaded"
                  message={describeImportError(rows.error).message}
                  onRetry={() => void rows.refetch()}
                />
              ) : rowList.length === 0 ? (
                <EmptyState
                  className="rounded-md border border-border bg-card shadow-sm"
                  title="No rows found"
                  description="No row matches this filter."
                />
              ) : (
                <RowTable
                  rows={rowList}
                  mode={mode}
                  selectedRowId={rowId}
                  onOpen={openRow}
                  hasMore={rows.hasNextPage}
                  loadingMore={rows.isFetchingNextPage}
                  onLoadMore={() => void rows.fetchNextPage()}
                />
              )}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
